import styled from "styled-components";
import { Flex } from "rebass";
import { useRecoilState, useSetRecoilState } from "recoil";
import { useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";

import { ESupportedGames } from "MVVM/@types";
import { GAME_TO_ICON_MAP } from "MVVM/Components/GameSelector";
import { MatchHistoryGame } from "MVVM/State/matchHistory";
import { UserGamePreference } from "MVVM/State/user";
import { useLinkedAccounts } from "MVVM/Hooks/useLinkedAccounts";

const GAME_LABELS: Partial<Record<ESupportedGames, string>> = {
  [ESupportedGames.CS2]: "CS2",
  [ESupportedGames.LOL]: "LoL",
  [ESupportedGames.FORTNITE]: "Fortnite",
  [ESupportedGames.DOTA2]: "Dota 2",
  [ESupportedGames.VALORANT]: "Valorant",
};

const GameSelector = () => {
  const [matchHistoryGame, setMatchHistoryGame] =
    useRecoilState(MatchHistoryGame);
  const { hasSteamLinked, hasRiotLinked, hasEpicLinked } = useLinkedAccounts();
  const setUserGamePreference = useSetRecoilState(UserGamePreference);
  const history = useHistory();
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const hasGameParam = params.get("game");

    if (matchHistoryGame === ESupportedGames.NONE && !hasGameParam) {
      if (hasSteamLinked) {
        setMatchHistoryGame(ESupportedGames.CS2);
      } else if (hasRiotLinked) {
        setMatchHistoryGame(ESupportedGames.LOL);
      } else if (hasEpicLinked) {
        setMatchHistoryGame(ESupportedGames.FORTNITE);
      }
    }
  }, [
    matchHistoryGame,
    hasRiotLinked,
    hasEpicLinked,
    hasSteamLinked,
    setMatchHistoryGame,
  ]);

  const onGameChange = (game: ESupportedGames) => {
    setMatchHistoryGame(game);
    setUserGamePreference(game);

    const params = new URLSearchParams(location.search);
    params.delete("game");
    params.delete("matchId");

    const newSearch = params.toString();
    const newUrl = `${location.pathname}${newSearch ? `?${newSearch}` : ""}`;

    if (newUrl !== `${location.pathname}${location.search}`) {
      history.replace(newUrl);
    }
  };

  const GAME_OPTIONS = [
    ESupportedGames.CS2,
    ESupportedGames.LOL,
    ESupportedGames.FORTNITE,
    ESupportedGames.DOTA2,
  ];

  return (
    <GameTabs>
      {GAME_OPTIONS.map((game) => (
        <GameTab
          key={game}
          $active={matchHistoryGame === game}
          onClick={() => onGameChange(game)}
        >
          {GAME_TO_ICON_MAP[game]}
          <span>{GAME_LABELS[game]}</span>
        </GameTab>
      ))}
    </GameTabs>
  );
};

const GameTabs = styled(Flex)`
  align-items: center;
  flex-wrap: wrap;
  gap: 18px;
`;

// Minimal on purpose: this is a quiet switcher, not a primary action, so the
// active game is marked with colour and an underline rather than a filled pill.
const GameTab = styled.button<{ $active: boolean }>`
  align-items: center;
  background: transparent;
  border: none;
  border-bottom: 2px solid
    ${({ theme, $active }) => ($active ? theme.colors.envy : "transparent")};
  border-radius: 0;
  color: ${({ theme, $active }) =>
    $active ? theme.colors.envy : theme.colors.chalk};
  cursor: pointer;
  display: inline-flex;
  font-family: Barlow;
  font-size: 14px;
  font-weight: 600;
  gap: 6px;
  padding: 4px 2px 5px;
  transition: color 0.15s ease;

  svg,
  img {
    opacity: ${({ $active }) => ($active ? 1 : 0.6)};
  }

  &:hover {
    color: ${({ theme }) => theme.colors.ice};
  }
`;

export { GameSelector };
